/**
 * Chip for a connection's registry state when it is not active: Provisioning,
 * Disabled, Zone not found or Zone renamed, with its one-line explanation.
 * Disabled and Zone not found are outlined, as in the VEG-862 design.
 */
import { Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';
import { useIntl } from 'react-intl';

import type { EhrSyncRegistryState } from '@models/ehr-sync';

import { registryStateMessages, registryStateNotes } from '../messages';
import type { EhrSyncTone } from './tone';
import { toneTextColor, toneTintColor } from './tone';

type InactiveState = Exclude<EhrSyncRegistryState, 'active'>;

/** Props for {@link RegistryStateChip}. */
interface RegistryStateChipProps {
  state: InactiveState;
}

const STATE_TONE: Record<InactiveState, EhrSyncTone> = {
  provisioning: 'grey',
  disabled: 'grey',
  zoneNotFound: 'error',
  zoneNameMismatch: 'warning',
};

const OUTLINED: ReadonlySet<InactiveState> = new Set([
  'disabled',
  'zoneNotFound',
]);

/**
 * Pill with the localized registry state and its note.
 *
 * @param props - see {@link RegistryStateChipProps}
 */
export default function RegistryStateChip({ state }: RegistryStateChipProps) {
  const { formatMessage } = useIntl();
  const tone = STATE_TONE[state];
  const outlined = OUTLINED.has(state);

  return (
    <Stack alignItems="flex-start" spacing={0.5}>
      <Typography
        component="span"
        variant="caption"
        fontWeight="bold"
        data-tone={tone}
        sx={(theme) => ({
          display: 'inline-flex',
          px: 1.1,
          py: 0.4,
          borderRadius: 999,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          color: toneTextColor(theme, tone),
          border: 1,
          borderColor: outlined
            ? alpha(toneTintColor(theme, tone), 0.6)
            : 'transparent',
          backgroundColor: outlined
            ? 'transparent'
            : alpha(toneTintColor(theme, tone), 0.14),
        })}
      >
        {formatMessage(registryStateMessages[state])}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {formatMessage(registryStateNotes[state])}
      </Typography>
    </Stack>
  );
}
